'use client';

// Bulk import screen — a customer who just joined uploads a 3-tab Excel
// (Corporate Clients, Individual Clients, Enquiries) and we load it all
// at once. Idiot-proof: download the ready-made template, fill it, upload.

import React, { useState } from "react";
import { Download, UploadCloud, CheckCircle2, FileSpreadsheet, RefreshCw, FileDown, Check, Circle, ArrowRight } from "lucide-react";
import { api, errText } from "./api";
import { useJigyasa } from "../jigyasa/state";

const FONT = { fontFamily: "'Hind', 'IBM Plex Sans', system-ui, sans-serif" };

const TABS = [
  { name: "Corporate Clients", cols: "Company name, Contact person, Phone, Email, City, Value won, Status, Notes" },
  { name: "Individual Clients", cols: "Name, Phone (10 digits), Email, City, Status, Notes" },
  { name: "Enquiries", cols: "Name, Phone (10 digits), Source, Type, Customer type, Company, Status, Notes" },
];

const ResultCard = ({ label, data }) => (
  <div data-testid={`import-result-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-xl border border-zinc-200 bg-white p-4">
    <div className="text-sm font-semibold text-zinc-900">{label}</div>
    <div className="mt-1 text-sm text-zinc-600">
      <span className="font-semibold text-emerald-700">{data.added} added</span>
      {data.skipped ? <span className="text-zinc-400"> · {data.skipped} skipped</span> : null}
    </div>
    {data.errors?.length > 0 && (
      <ul className="mt-2 space-y-0.5 text-xs text-amber-700">
        {data.errors.slice(0, 6).map((e, i) => <li key={i}>• {e}</li>)}
        {data.errors.length > 6 && <li>• …and {data.errors.length - 6} more</li>}
      </ul>
    )}
  </div>
);

export const ImportData = ({ currentUser, onImported, onNavigate }) => {
  const isAdmin = ["owner", "admin"].includes(currentUser?.role);
  const { stages, enquiryTypes, orgSettings } = useJigyasa();
  const sources = orgSettings?.sources || [];
  // Setup-order guardrail: an owner should have their board basics in place
  // before bringing leads in, so every imported lead lands somewhere real.
  const checks = [
    { key: "stages", ok: (stages?.length || 0) > 0, label: "Your follow-up steps" },
    { key: "types", ok: (enquiryTypes?.length || 0) > 0, label: "Your products / services" },
    { key: "sources", ok: sources.length > 0, label: "Where enquiries come from" },
  ];
  const ready = checks.every((c) => c.ok);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    setError("");
    try {
      const res = await api.get("/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "jigyasa_import_template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(errText(e));
    }
  };

  const downloadReport = async () => {
    setError("");
    setReportBusy(true);
    try {
      const res = await api.get("/import/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `jigyasa_report_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(errText(e));
    } finally {
      setReportBusy(false);
    }
  };

  const upload = async () => {
    if (!file) { setError("Please choose your filled-in Excel file first."); return; }
    setError("");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/excel", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      onImported?.();
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="import-page" className="mx-auto max-w-3xl px-4 py-6 sm:px-6" style={FONT}>
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Import your existing data</h1>
      <p className="mt-1 text-sm text-zinc-500">
        Bring your customers and enquiries in one go from an Excel file.
      </p>

      {!isAdmin ? (
        <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-5 text-sm text-zinc-600">
          Only the owner or an admin can import data.
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          {/* Before you import — setup-order readiness check */}
          <div
            data-testid="import-prereq"
            data-tour-id="jigyasa-import-readiness"
            className={`rounded-2xl border p-5 ${ready ? "border-emerald-200 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              {ready ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} className="text-amber-500" />}
              Before you import
            </div>
            <p className="mt-1 text-sm text-zinc-600">
              {ready
                ? "Your board is ready — your follow-up steps, products and sources are all set. Imported leads will land in the right place."
                : "Set up these basics first so every imported lead lands somewhere real."}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {checks.map((c) => (
                <div
                  key={c.key}
                  data-testid={`import-prereq-${c.key}`}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium ${
                    c.ok ? "border-emerald-200 bg-white text-emerald-800" : "border-amber-300 bg-white text-amber-800"
                  }`}
                >
                  {c.ok ? <Check size={14} className="text-emerald-600" /> : <Circle size={14} className="text-amber-500" />}
                  {c.label}
                </div>
              ))}
            </div>
            {!ready && (
              <button
                data-testid="import-prereq-settings"
                onClick={() => onNavigate?.("settings")}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-700"
              >
                Take me there <ArrowRight size={14} />
              </button>
            )}
          </div>

          {/* Step 1 — template */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-xs text-white">1</span>
              Download the ready-made template
            </div>
            <p className="mt-2 text-sm text-zinc-500">
              It already has the three tabs and the right column headings — just fill in your rows.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {TABS.map((t) => (
                <div key={t.name} className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-800">
                    <FileSpreadsheet size={14} className="text-rose-600" /> {t.name}
                  </div>
                  <div className="mt-1 text-[11px] leading-snug text-zinc-500">{t.cols}</div>
                </div>
              ))}
            </div>
            <button
              data-testid="download-template-btn"
              data-tour-id="jigyasa-import-template"
              onClick={downloadTemplate}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-zinc-800 hover:bg-zinc-100"
            >
              <Download size={16} /> Download Excel template
            </button>
          </div>

          {/* Step 2 — upload */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-xs text-white">2</span>
              Upload your filled-in file
            </div>
            <p className="mt-2 text-sm text-zinc-500">
              In the Enquiries tab, put the stage in the <strong>Status</strong> column (e.g. New, Spoke to them,
              Sent quote, Won, Lost). Existing rows aren’t touched — duplicates are skipped.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <input
                data-testid="import-file-input"
                type="file"
                accept=".xlsx"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
                className="text-sm text-zinc-700 file:mr-3 file:rounded-lg file:border-0 file:bg-zinc-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-zinc-700 hover:file:bg-zinc-200"
              />
              <button
                data-testid="import-upload-btn"
                data-tour-id="jigyasa-import-upload"
                onClick={upload}
                disabled={busy || !ready}
                title={ready ? "" : "Set up your board basics first"}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                <UploadCloud size={16} /> {busy ? "Importing…" : "Upload and import"}
              </button>
            </div>
            {!ready && (
              <p data-testid="import-gate-note" className="mt-2 text-xs text-amber-700">
                Finish "Before you import" above first.
              </p>
            )}
            {error && (
              <div data-testid="import-error" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </div>
            )}
          </div>

          {/* Step 3 — download a full Excel report */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-xs text-white">3</span>
              Download a report of everything
            </div>
            <p className="mt-2 text-sm text-zinc-500">
              Get one Excel file with a summary of your numbers plus every enquiry,
              corporate customer and individual customer — handy for your records or your accountant.
            </p>
            <button
              data-testid="download-report-btn"
              onClick={downloadReport}
              disabled={reportBusy}
              className="mt-4 inline-flex items-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-zinc-800 hover:bg-zinc-100 disabled:opacity-50"
            >
              <FileDown size={16} /> {reportBusy ? "Preparing…" : "Download Excel report"}
            </button>
          </div>

          {/* Result */}
          {result && (
            <div data-testid="import-result" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                <CheckCircle2 size={18} className="text-emerald-600" /> {result.message}
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <ResultCard label="Corporate" data={result.corporate} />
                <ResultCard label="Individuals" data={result.individuals} />
                <ResultCard label="Enquiries" data={result.enquiries} />
              </div>
              <button
                data-testid="import-reload-btn"
                onClick={() => window.location.reload()}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-700"
              >
                <RefreshCw size={15} /> Refresh to see your data
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
